import { Deal } from '../../types';
import { formatCurrency } from '../../utils/formatters';

export class DocumentGenerator {
  private static instance: DocumentGenerator;
  private templates: Record<string, string>;

  private constructor() {
    this.templates = {
      loi: this.getLoiTemplate(),
      termSheet: this.getTermSheetTemplate(),
      dueDiligence: this.getDueDiligenceTemplate(),
      boardPresentation: this.getBoardPresentationTemplate(),
      integrationPlan: this.getIntegrationPlanTemplate(),
      closingChecklist: this.getClosingChecklistTemplate()
    };
  }

  public static getInstance(): DocumentGenerator {
    if (!DocumentGenerator.instance) {
      DocumentGenerator.instance = new DocumentGenerator();
    }
    return DocumentGenerator.instance;
  }

  public async generateDocument(type: keyof typeof this.templates, deal: Deal): Promise<string> {
    const template = this.templates[type];
    if (!template) {
      throw new Error(`Template not found for type: ${type}`);
    }
    return this.populateTemplate(template, deal);
  }

  private async populateTemplate(template: string, deal: Deal): Promise<string> {
    return template
      .replace(/\${dealName}/g, deal.name)
      .replace(/\${purchasePrice}/g, formatCurrency(deal.value))
      .replace(/\${targetCompany}/g, deal.targetCompany.name)
      .replace(/\${acquiringCompany}/g, deal.acquiringCompany.name)
      .replace(/\${closingDate}/g, new Date(deal.expectedCloseDate).toLocaleDateString())
      .replace(/\${industry}/g, deal.targetCompany.industry);
  }

  private getLoiTemplate(): string {
    return `
[Company Letterhead]

${new Date().toLocaleDateString()}

STRICTLY CONFIDENTIAL

[Recipient Name]
[Company Name]
[Address]

Re: Letter of Intent - Proposed Acquisition of \${targetCompany}

Dear [Name],

This Letter of Intent ("LOI") outlines the principal terms under which \${acquiringCompany} ("Buyer") proposes to acquire \${targetCompany} ("Target").

1. Transaction Structure
   The proposed transaction will be structured as [an asset/stock] purchase for a total consideration of \${purchasePrice}.

2. Purchase Price
   The purchase price will be paid as follows:
   - Cash at closing: [Amount]
   - Seller note: [Amount]
   - Earnout: [Terms]

3. Due Diligence
   Buyer will conduct customary due diligence for a period of [X] days following the execution of this LOI.

4. Exclusivity
   Target agrees to negotiate exclusively with Buyer for a period of [X] days.

5. Confidentiality
   This LOI and all discussions related to the proposed transaction shall remain confidential.

6. Binding Effect
   This LOI is non-binding except for the Exclusivity and Confidentiality provisions.

Sincerely,
[Name]
[Title]
\${acquiringCompany}

ACCEPTED AND AGREED:
\${targetCompany}

By: _____________________
Name:
Title:
Date:`;
  }

  private getTermSheetTemplate(): string {
    return `
TERM SHEET
\${dealName}

Transaction Overview:
-------------------
Buyer: \${acquiringCompany}
Target: \${targetCompany}
Purchase Price: \${purchasePrice}
Industry: \${industry}
Expected Closing: \${closingDate}

Key Terms:
---------
1. Structure
   - Transaction type: [Merger/Asset Purchase/Stock Purchase]
   - Consideration: Cash and stock mix
   - Earnout provisions

2. Valuation
   - Enterprise Value: \${purchasePrice}
   - Multiple: [X]x EBITDA
   - Working capital adjustments

3. Conditions to Close
   - Board approval
   - Shareholder approval
   - Regulatory clearance
   - Third-party consents

4. Representations & Warranties
   - Customary for transactions of this nature
   - Survival period: [X] months
   - Cap: [X]% of purchase price
   - Basket: [X]% of purchase price

5. Covenants
   - Operating covenants
   - Non-compete provisions
   - Employee matters

6. Closing Conditions
   - Due diligence satisfaction
   - No material adverse change
   - Required approvals obtained

7. Timeline
   - Sign definitive agreement: [Date]
   - Close transaction: \${closingDate}

This term sheet is non-binding and subject to further negotiation and documentation.`;
  }

  private getDueDiligenceTemplate(): string {
    return `
DUE DILIGENCE REPORT
\${dealName}

Executive Summary:
----------------
Target Company: \${targetCompany}
Industry: \${industry}
Transaction Value: \${purchasePrice}

1. Financial Review
   ---------------
   - Historical performance analysis
   - Working capital analysis
   - Cash flow projections
   - Debt and capital structure
   - Quality of earnings assessment

2. Operational Assessment
   --------------------
   - Business model evaluation
   - Market position analysis
   - Customer concentration
   - Supply chain review
   - IT systems assessment

3. Legal & Regulatory
   ----------------
   - Corporate structure
   - Material contracts
   - Litigation review
   - Regulatory compliance
   - IP portfolio assessment

4. Human Resources
   --------------
   - Organization structure
   - Key personnel
   - Compensation & benefits
   - Employment agreements
   - Culture assessment

5. Risk Factors
   -----------
   - Market risks
   - Operational risks
   - Financial risks
   - Legal risks
   - Integration risks

6. Synergy Opportunities
   -------------------
   - Cost synergies
   - Revenue synergies
   - Operational efficiencies
   - Timeline for realization

7. Recommendations
   --------------
   - Valuation implications
   - Deal structure considerations
   - Key findings and red flags
   - Next steps

This report is confidential and for internal use only.`;
  }

  private getBoardPresentationTemplate(): string {
    return `
BOARD PRESENTATION
\${dealName}

1. Transaction Overview
   ------------------
   - Target: \${targetCompany}
   - Industry: \${industry}
   - Purchase Price: \${purchasePrice}
   - Structure: [Transaction Type]
   - Expected Close: \${closingDate}

2. Strategic Rationale
   -----------------
   - Market expansion
   - Product portfolio enhancement
   - Technology acquisition
   - Operational synergies

3. Financial Analysis
   ----------------
   - Valuation metrics
   - Synergy potential
   - Return analysis
   - Funding structure

4. Due Diligence Summary
   -------------------
   - Financial findings
   - Operational assessment
   - Legal review
   - Risk factors

5. Integration Planning
   -----------------
   - Integration strategy
   - Key milestones
   - Resource requirements
   - Risk mitigation

6. Transaction Timeline
   -----------------
   - Key dates and deadlines
   - Critical path items
   - Required approvals

7. Board Considerations
   -----------------
   - Strategic alignment
   - Risk assessment
   - Required approvals
   - Next steps

Appendix:
- Detailed financial models
- Due diligence reports
- Integration plans
- Risk analysis`;
  }

  private getIntegrationPlanTemplate(): string {
    return `
INTEGRATION PLAN
\${dealName}

1. Executive Summary
   ---------------
   Deal Overview:
   - Target: \${targetCompany}
   - Value: \${purchasePrice}
   - Close Date: \${closingDate}

2. Integration Strategy
   -----------------
   - Vision and objectives
   - Integration approach
   - Governance structure
   - Communication strategy

3. Workstreams
   ----------
   a) Finance & Accounting
      - Systems integration
      - Process harmonization
      - Reporting alignment

   b) Operations
      - Facility consolidation
      - Supply chain optimization
      - Technology integration

   c) Human Resources
      - Organization structure
      - Retention strategy
      - Culture alignment
      - Benefits harmonization

   d) IT Systems
      - Infrastructure
      - Applications
      - Data migration
      - Security

4. Synergy Realization
   -----------------
   - Cost synergies
   - Revenue synergies
   - Implementation timeline
   - Tracking mechanisms

5. Risk Management
   -------------
   - Key risks identified
   - Mitigation strategies
   - Contingency plans

6. Timeline & Milestones
   ------------------
   Day 1:
   - Critical operations
   - Key communications
   - Essential systems

   First 100 Days:
   - Integration priorities
   - Quick wins
   - Key decisions

   Year 1:
   - Major initiatives
   - System consolidations
   - Process harmonization

7. Success Metrics
   -------------
   - KPIs by workstream
   - Synergy tracking
   - Integration milestones`;
  }

  private getClosingChecklistTemplate(): string {
    return `
CLOSING CHECKLIST
\${dealName}

Transaction Details:
------------------
Buyer: \${acquiringCompany}
Target: \${targetCompany}
Closing Date: \${closingDate}
Purchase Price: \${purchasePrice}

A. Corporate Documents
   -----------------
   □ Board resolutions
   □ Shareholder approvals
   □ Secretary certificates
   □ Good standing certificates
   □ Corporate authority documents

B. Transaction Documents
   ------------------
   □ Purchase agreement
   □ Disclosure schedules
   □ Officer certificates
   □ Legal opinions
   □ Escrow agreement

C. Due Diligence Items
   -----------------
   □ Financial statements
   □ Material contracts
   □ Employee agreements
   □ IP documentation
   □ Real estate documents

D. Regulatory/Third Party
   -------------------
   □ HSR clearance
   □ Industry-specific approvals
   □ Third-party consents
   □ Governmental approvals

E. Financial Items
   -------------
   □ Funds transfer instructions
   □ Payoff letters
   □ Lien releases
   □ Working capital statement
   □ Purchase price allocation

F. Post-Closing Items
   ----------------
   □ Press release
   □ Employee communications
   □ Customer notifications
   □ Integration kickoff
   □ Systems access

Status Key:
□ Not Started
▣ In Progress
■ Completed
⨯ Not Applicable`;
  }
}